import { useTranslation } from 'react-i18next'
import { Alpha3Code } from 'i18n-iso-countries'
import { Link } from 'react-router-dom'

import { Badge, CardContent } from '@percent/lemonade'
import { BankDetailsMainView } from '@percent/admin-dashboard/api/actions/bankDetails/bankDetails.types'
import { PaymentMethod } from '@percent/admin-dashboard/api/types'
import { PaymentTypeWithButton } from './PaymentTypeWithButton'
import { BankDetailStatus, getBankDetailsFields } from '@percent/domain/bank-details'

const apiBankDetailsReviewStatusToDisplayName = {
  pending: 'Pending',
  approved: 'Approved'
}

export function BankDetailsView({
  paymentMethod,
  paymentMethodRefresh,
  bankDetailsData,
  organisationCountryCode
}: Readonly<{
  paymentMethod?: PaymentMethod
  paymentMethodRefresh: VoidFunction
  bankDetailsData: BankDetailsMainView
  organisationCountryCode: Alpha3Code
}>) {
  const { t } = useTranslation()

  const reviewUrl = `/bank-details-reviews/${bankDetailsData?.id}`

  const wireTransferDetails = bankDetailsData.bankAccountDetails?.wireTransferDetails
  const fields = wireTransferDetails ? getBankDetailsFields(wireTransferDetails) : []

  const bankTransferPaymentMethod = (
    <CardContent
      title={
        paymentMethod?.method === 'card' || paymentMethod?.method === 'cheque'
          ? t('typography.newPaymentMethod')
          : t('typography.paymentMethod')
      }
      testId="bank-details-payment-method"
    >
      {t('typography.bankTransfer')}
    </CardContent>
  )

  const isBankDetailsApprovedOrPending =
    bankDetailsData?.status === BankDetailStatus.APPROVED || bankDetailsData?.status === BankDetailStatus.PENDING

  if (isBankDetailsApprovedOrPending) {
    const isApproved = bankDetailsData?.status === BankDetailStatus.APPROVED
    const statusLabel = isApproved
      ? apiBankDetailsReviewStatusToDisplayName.approved
      : apiBankDetailsReviewStatusToDisplayName.pending

    return (
      <>
        {bankTransferPaymentMethod}
        <CardContent title={t('typography.status')} testId="bank-details-status">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <Badge
              variant={isApproved ? 'positive' : 'default'}
              icon={isApproved ? 'approve' : 'clock'}
              testId="bank-details-status-badge"
            >
              {statusLabel}
            </Badge>
          </div>
        </CardContent>
        <CardContent title={t('typography.accountHolderName')} testId="bank-details-holder">
          {bankDetailsData?.bankAccountDetails?.holderName || t('typography.notAvailable')}
        </CardContent>
        {fields.map(field => (
          <CardContent key={field.key} title={field.label} testId={`bank-details-${field.key}`}>
            {field?.value || t('typography.notAvailable')}
          </CardContent>
        ))}
        <CardContent title={t('typography.seeBankDetailsReview')}>
          <Link data-testId="go-to-review-page-button" to={reviewUrl} title={t('button.seeReview')}>
            {t('button.seeReview')}
          </Link>
        </CardContent>
      </>
    )
  }

  if (paymentMethod?.method === 'bank_transfer') {
    return (
      <>
        {bankTransferPaymentMethod}
        <CardContent title={t('typography.noneSupplied')} />
      </>
    )
  }

  return (
    <PaymentTypeWithButton
      title={t('typography.paymentMethod')}
      text={t('typography.noneSupplied')}
      refresh={paymentMethodRefresh}
      organisationCountryCode={organisationCountryCode}
    />
  )
}
